import { set } from 'lodash';
import { TEvent } from 'types/event';

import { NodeExtended } from '@components/common/tree/common/types';
import { TSchedule } from '@components/schedule/common/types';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

interface CalendarState {
    selectedEntities: NodeExtended | null;
    selectedOrderId: number | null;
    selectedDate: string;
    timeline: TEvent[];
    schedules: TSchedule[];
    viewMode: string;
    history: Array<{
        selectedEntities: NodeExtended | null;
        selectedOrderId: number | null;
        selectedDate: string;
        viewMode: string;
    }>;
    historyIndex: number;
}

const initialState: CalendarState = {
    selectedEntities: null,
    selectedOrderId: null,
    selectedDate: new Date().toISOString(),
    timeline: [],
    schedules: [],
    viewMode: 'month',
    history: [],
    historyIndex: -1,
};

const isStateEqual = (state: CalendarState, snapshot: CalendarState['history'][0]) => {
    return (
        state.selectedDate === snapshot.selectedDate &&
        state.viewMode === snapshot.viewMode &&
        state.selectedOrderId === snapshot.selectedOrderId &&
        JSON.stringify(state.selectedEntities) === JSON.stringify(snapshot.selectedEntities)
    );
};

const calendarSlice = createSlice({
    name: 'calendar',
    initialState,
    reducers: {
        _pushHistory: (state) => {
            const snapshot = {
                selectedEntities: state.selectedEntities ? { ...state.selectedEntities } : null,
                selectedOrderId: state.selectedOrderId,
                selectedDate: state.selectedDate,
                viewMode: state.viewMode,
            };

            const lastSnapshot = state.history[state.historyIndex];
            if (lastSnapshot && isStateEqual(snapshot as any, lastSnapshot)) {
                return;
            }

            if (state.historyIndex < state.history.length - 1) {
                state.history = state.history.slice(0, state.historyIndex + 1);
            }

            state.history.push(snapshot);
            state.historyIndex = state.history.length - 1;
        },

        setSelectedEntities: (state, action: PayloadAction<NodeExtended>) => {
            state.selectedEntities = action.payload;
        },

        setSelectedOrderId: (state, action: PayloadAction<number | null>) => {
            state.selectedOrderId = action.payload;
        },

        setSelectedDate: (state, action: PayloadAction<string>) => {
            state.selectedDate = action.payload;
        },

        setViewMode: (state, action: PayloadAction<string>) => {
            state.viewMode = action.payload;
        },

        setTimeLine: (state, action: PayloadAction<TEvent[]>) => {
            state.timeline = action.payload;
        },

        setSchedules: (state, action: PayloadAction<TSchedule[]>) => {
            state.schedules = action.payload;
        },

        undo: (state) => {
            if (state.historyIndex > 0) {
                state.historyIndex -= 1;
                const snapshot = state.history[state.historyIndex];
                state.selectedEntities = snapshot.selectedEntities;
                state.selectedOrderId = snapshot.selectedOrderId;
                state.selectedDate = snapshot.selectedDate;
                state.viewMode = snapshot.viewMode;
            }
        },

        redo: (state) => {
            if (state.historyIndex < state.history.length - 1) {
                state.historyIndex += 1;
                const snapshot = state.history[state.historyIndex];
                state.selectedEntities = snapshot.selectedEntities;
                state.selectedOrderId = snapshot.selectedOrderId;
                state.selectedDate = snapshot.selectedDate;
                state.viewMode = snapshot.viewMode;
            }
        },
    },
});

export const {
    _pushHistory,
    setSelectedEntities,
    setSelectedOrderId,
    setSelectedDate,
    setTimeLine,
    setSchedules,
    setViewMode,
    undo,
    redo,
} = calendarSlice.actions;

export default calendarSlice.reducer;
